import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

// Returns the Monday (YYYY-MM-DD) of the *previous* IST week — the last fully
// completed cycle, which is what the admin usually wants to settle.
function lastWeekMondayIso() {
  const now = new Date();
  const day = now.getDay(); // 0 Sun..6 Sat
  const diffToMonday = (day === 0 ? 6 : day - 1) + 7;
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diffToMonday);
  const y = monday.getFullYear();
  const m = String(monday.getMonth() + 1).padStart(2, "0");
  const d = String(monday.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function StatusPill({ status }) {
  const cls = status === "paid" ? "bg-green-100 text-green-700 border-green-200" : "bg-amber-100 text-amber-700 border-amber-200";
  return <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-full ${cls}`}>{status || "pending"}</span>;
}

export default function AdminPayouts() {
  const [week, setWeek] = useState(lastWeekMondayIso());
  const [statusFilter, setStatusFilter] = useState("all");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [payFor, setPayFor] = useState(null); // record being marked paid
  const [utr, setUtr] = useState("");
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [adjustFor, setAdjustFor] = useState(null);
  const [breakdownFor, setBreakdownFor] = useState(null);
  const [adjustBusy, setAdjustBusy] = useState(false);
  const [otherLabel, setOtherLabel] = useState("");
  const [otherAmount, setOtherAmount] = useState("");
  const [gatewayPct, setGatewayPct] = useState("");
  const [tcsVal, setTcsVal] = useState("");
  useEffect(() => {
    if (!adjustFor) return;
    setGatewayPct(String(adjustFor.gateway_fee_pct ?? ""));
    setTcsVal(String(adjustFor.tcs ?? ""));
    setOtherLabel(""); setOtherAmount("");
  }, [adjustFor]);
  const submitAdjust = async () => {
    if (!adjustFor) return;
    setAdjustBusy(true);
    try {
      const body = {};
      if (gatewayPct !== "" && Number(gatewayPct) !== adjustFor.gateway_fee_pct) body.gateway_fee_pct = Number(gatewayPct);
      if (tcsVal !== "" && Number(tcsVal) !== adjustFor.tcs) body.tcs = Number(tcsVal);
      if (otherLabel.trim() && Number(otherAmount) > 0) body.add_other_deduction = { label: otherLabel.trim(), amount: Number(otherAmount) };
      if (!Object.keys(body).length) { toast.error("Nothing to save"); setAdjustBusy(false); return; }
      const { data } = await api.patch(`/admin/payouts/${adjustFor.id}/adjust`, body);
      toast.success(`Net payable now ₹${Number(data.net_payable || 0).toLocaleString("en-IN")}`);
      setAdjustFor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not adjust"); }
    finally { setAdjustBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { week_start: week };
      if (statusFilter !== "all") params.pay_status = statusFilter;
      const { data } = await api.get("/admin/payouts", { params });
      setRecords(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load payouts");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [week, statusFilter]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/payouts/generate", { week_start: week });
      toast.success(`Generated ${data.count} record(s) for ${data.week_label}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate");
    } finally { setGenerating(false); }
  };

  const openPay = (r) => {
    setPayFor(r);
    setUtr("");
    setPaidAt(new Date().toISOString().slice(0, 10));
  };

  const savePay = async () => {
    if (!utr.trim()) return toast.error("Please enter the UTR / reference number");
    setSaving(true);
    try {
      await api.post(`/admin/payouts/${payFor.id}/pay`, { utr: utr.trim(), paid_at: paidAt });
      toast.success(`Marked ${payFor.business_name} as paid. Vendor notified.`);
      setPayFor(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    } finally { setSaving(false); }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (week) params.set("week_start", week);
      if (statusFilter !== "all") params.set("pay_status", statusFilter);
      const res = await api.get(`/admin/payouts/export.csv?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cityshor_payouts_${week || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to export CSV"); }
  };

  const totals = useMemo(() => records.reduce((acc, r) => {
    acc.gross += Number(r.gross_sales || 0);
    acc.commission += Number(r.commission_amount || 0);
    acc.net += Number(r.net_payout || 0);
    if (r.status === "paid") acc.paid += Number(r.net_payout || 0);
    else acc.pending += Number(r.net_payout || 0);
    return acc;
  }, { gross: 0, commission: 0, net: 0, paid: 0, pending: 0 }), [records]);

  const weekLabel = records[0]?.week_label;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-display text-xl">Weekly Payout Settlements</div>
          <div className="text-sm text-gray-500">Weekly cycle runs Monday to Sunday (IST). Generate the sheet, then mark each vendor Paid with the UTR after your bank transfer.</div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[11px]">Week starting (Mon)</Label>
            <Input data-testid="payouts-week" type="date" value={week} onChange={(e) => setWeek(e.target.value)} className="h-9 w-40 text-sm" />
          </div>
          <div>
            <Label className="text-[11px]">Status</Label>
            <select data-testid="payouts-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 border border-gray-300 rounded-md px-2 text-sm">
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <Button data-testid="payouts-generate" onClick={generate} disabled={generating} className="rounded-full h-9 bg-[#F48E2E] hover:bg-[#D97815]">
            <RefreshCw className={`h-4 w-4 mr-1 ${generating ? "animate-spin" : ""}`} /> Generate for this week
          </Button>
          <Button data-testid="payouts-export" onClick={exportCsv} variant="outline" className="rounded-full h-9">
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {weekLabel && (
        <div className="text-xs text-gray-500 mb-3" data-testid="payouts-week-label">Showing settlements for <b className="text-gray-700">{weekLabel}</b></div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {[
          { l: "Vendors", v: records.length, key: "count" },
          { l: "Gross sales", v: money(totals.gross), key: "gross" },
          { l: "Commission", v: money(totals.commission), key: "commission" },
          { l: "Net paid", v: money(totals.paid), key: "paid", tone: "text-green-700" },
          { l: "Net pending", v: money(totals.pending), key: "pending", tone: "text-amber-700" },
        ].map((s) => (
          <div key={s.key} data-testid={`payouts-stat-${s.key}`} className="p-3 bg-white border border-gray-200 rounded-xl">
            <div className="text-[11px] uppercase tracking-widest text-gray-500">{s.l}</div>
            <div className={`font-display text-lg mt-1 ${s.tone || ""}`}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-3">Vendor</th>
              <th className="p-3">Code</th>
              <th className="p-3">Bank details</th>
              <th className="p-3 text-right">Orders</th>
              <th className="p-3 text-right">Gross</th>
              <th className="p-3 text-right">Packaging</th>
              <th className="p-3 text-right">Commission</th>
              <th className="p-3 text-right">Returns</th>
              <th className="p-3 text-right">Refunds</th>
              <th className="p-3 text-right">Gateway</th>
              <th className="p-3 text-right">Shipping</th>
              <th className="p-3 text-right">Admin refund</th>
              <th className="p-3 text-right">Other</th>
              <th className="p-3 text-right">Net payable</th>
              <th className="p-3">Status</th>
              <th className="p-3">UTR</th>
              <th className="p-3">Paid on</th>
              <th className="p-3 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={18} className="p-8 text-center text-gray-500">Loading…</td></tr>
            ) : records.length === 0 ? (
              <tr><td colSpan={18} className="p-8 text-center text-gray-500">No settlements for this filter. Click <b>Generate for this week</b> to compute payouts from the orders in the selected week.</td></tr>
            ) : records.map((r) => (
              <tr key={r.id} data-testid={`payout-row-${r.id}`} className="border-t">
                <td className="p-3">
                  <div className="font-medium">{r.business_name}</div>
                  <div className="text-[11px] text-gray-500">{r.owner_email}</div>
                </td>
                <td className="p-3 font-mono-tab text-xs text-[#F48E2E]">{r.vendor_code || "-"}</td>
                <td className="p-3 text-xs">
                  {r.bank_account_number ? (
                    <div>
                      <div>{r.bank_holder_name || "—"}</div>
                      <div className="font-mono-tab text-gray-500">{r.bank_account_number} · {r.bank_ifsc}</div>
                    </div>
                  ) : <span className="text-gray-400">Bank details missing</span>}
                </td>
                <td className="p-3 text-right font-mono-tab">{r.order_count}</td>
                <td className="p-3 text-right font-mono-tab">{money(r.gross_sales)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">{money(r.packaging_charges_collected)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">-{money(r.commission_amount)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">-{money(r.returns_deducted)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">-{money(r.refunds_deducted)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">-{money(r.gateway_fee)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500" title="Actual Shiprocket freight recovered on free-delivery orders">-{money(r.shipping_deducted || 0)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">-{money(r.admin_refund_deduction)}</td>
                <td className="p-3 text-right font-mono-tab text-gray-500">-{money(r.other_deductions_total)}</td>
                <td className="p-3 text-right font-mono-tab font-medium">{money(r.net_payable || r.net_payout)}</td>
                <td className="p-3"><StatusPill status={r.status} /></td>
                <td className="p-3 font-mono-tab text-xs">{r.utr || "-"}</td>
                <td className="p-3 text-xs">{r.paid_at || "-"}</td>
                <td className="p-3 text-center">
                  <div className="flex gap-1 justify-center">
                    <Button
                      data-testid={`payout-breakdown-${r.id}`}
                      size="sm"
                      onClick={() => setBreakdownFor(r)}
                      variant="outline"
                      className="rounded-full h-8 text-xs"
                    >Orders</Button>
                    <Button
                      data-testid={`payout-adjust-${r.id}`}
                      size="sm"
                      disabled={r.status === "paid"}
                      onClick={() => setAdjustFor(r)}
                      variant="outline"
                      className="rounded-full h-8 text-xs disabled:opacity-40"
                    >Adjust</Button>
                    <Button
                      data-testid={`payout-pay-${r.id}`}
                      size="sm"
                      disabled={r.status === "paid" || !r.bank_account_number}
                      onClick={() => openPay(r)}
                      className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white h-8 text-xs disabled:opacity-40"
                    >{r.status === "paid" ? "Paid" : "Mark Paid"}</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!breakdownFor} onOpenChange={(v) => !v && setBreakdownFor(null)}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle>Per-order payout breakdown — {breakdownFor?.business_name}</DialogTitle>
          </DialogHeader>
          {breakdownFor && (
            <div className="text-sm" data-testid="payout-breakdown-dialog">
              {(breakdownFor.order_breakdowns || []).length === 0 ? (
                <p className="text-gray-500 py-4">Per-order breakdown is available for payouts generated after this update. Regenerate this week to compute it.</p>
              ) : (
                <table className="w-full text-xs mt-2">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="p-2">Order</th>
                      <th className="p-2 text-right">Sale amount</th>
                      <th className="p-2 text-right">Shipping deduction</th>
                      <th className="p-2 text-right">Commission</th>
                      <th className="p-2 text-right">GST on commission</th>
                      <th className="p-2 text-right">Final payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(breakdownFor.order_breakdowns || []).map((b) => (
                      <tr key={b.order_id} data-testid={`breakdown-row-${b.order_id}`} className="border-t">
                        <td className="p-2 font-mono-tab">{String(b.order_id || "").slice(0, 8).toUpperCase()}</td>
                        <td className="p-2 text-right font-mono-tab">{money(b.sale_amount)}</td>
                        <td className="p-2 text-right font-mono-tab text-gray-500">{Number(b.shipping_deduction || 0) > 0 ? `-${money(b.shipping_deduction)}` : "—"}</td>
                        <td className="p-2 text-right font-mono-tab text-gray-500">-{money(b.commission)}</td>
                        <td className="p-2 text-right font-mono-tab text-gray-500">{Number(b.gst_on_commission || 0) > 0 ? `-${money(b.gst_on_commission)}` : "—"}</td>
                        <td className="p-2 text-right font-mono-tab font-medium">{money(b.net_payout)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="text-[11px] text-gray-500 mt-3">Shipping deduction applies only when the buyer got free delivery — CityShor recovers the actual Shiprocket freight it paid for that parcel.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!payFor} onOpenChange={(v) => !v && setPayFor(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Mark payout as paid</DialogTitle>
          </DialogHeader>
          {payFor && (
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-[#FEF3E6] border border-[#F48E2E]/30 rounded-xl">
                <div><b>{payFor.business_name}</b> — <span className="text-[#F48E2E] font-mono-tab">{payFor.vendor_code}</span></div>
                <div className="text-xs text-gray-600 mt-1">{payFor.week_label} · Net payout <b>{money(payFor.net_payout)}</b></div>
                {payFor.bank_holder_name && (
                  <div className="text-xs text-gray-600 mt-1">Transfer to {payFor.bank_holder_name} · <span className="font-mono-tab">{payFor.bank_account_number}</span> · {payFor.bank_ifsc}</div>
                )}
              </div>
              <div>
                <Label>UTR / Reference number <span className="text-red-500">*</span></Label>
                <Input data-testid="pay-utr" value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. NEFT1234567890" className="h-10" />
              </div>
              <div>
                <Label>Date of transfer</Label>
                <Input data-testid="pay-date" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="h-10" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setPayFor(null)} className="rounded-full">Cancel</Button>
                <Button data-testid="pay-save" onClick={savePay} disabled={saving} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">
                  {saving ? "Saving…" : "Confirm & notify vendor"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
